import { useEffect, useState } from 'react';
import Header from '@/component/Header';
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import { readStakeDbDashboardUserList } from '@/api/stake-db';
import { readStakeDbPoolTitles } from '@/api/stake-db';
import { StakeDbDashboardRequest, StakeDbDashboardResponse } from '@/types/stake-db';

import { z as zod } from 'zod';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import OutlinedInput from '@mui/material/OutlinedInput';
import Stack from '@mui/material/Stack';
import DatePickers from '@/component/date-pickers';
import Grid from '@mui/material/Unstable_Grid2';

import StakeList from '@/component/stake-db/dashboard/StakeList';
import UnstakeList from '@/component/stake-db/dashboard/UnstakeList';
import RewardList from '@/component/stake-db/dashboard/RewardList';
import WithdrawList from '@/component/stake-db/dashboard/WithdrawList';

const statusList = [
  { label: 'stake', value: '0' },
  { label: 'unstake', value: '1' },
  { label: 'withdraw', value: '2' },
  { label: 'reward', value: '3' },
];

const schema = zod.object({
  walletAddress: zod.string().optional(),
  title: zod.string().optional(),
  stakeState: zod.string().optional(),
});

type Values = zod.infer<typeof schema>;

const defaultValues = { walletAddress: '', title: '', stakeState: '' } satisfies Values;

export default function StakeDbDashboard() {
  const [list, setList] = useState<StakeDbDashboardResponse | null>(null);
  const [titles, setTitles] = useState<string[]>([]);
  const [date, setDate] = useState({
    startDt: '',
    endDt: '',
  });
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<Values>({ defaultValues, resolver: zodResolver(schema) });

  useEffect(() => {
    const fetchTitles = async () => {
      try {
        const re = await readStakeDbPoolTitles();
        if (re.status === 200) {
          setTitles(re.data.titles || []);
        }
      } catch (error) {
        console.error('Failed to fetch pool titles:', error);
      }
    };
    fetchTitles();
  }, []);

  const onSubmit = async (data: Values) => {
    const filteredData = Object.fromEntries(
      Object.entries(data).filter(([_, value]) => value !== '' && value !== undefined && value !== null),
    );
    const filteredDate = Object.fromEntries(
      Object.entries(date).filter(([_, value]) => value !== '' && value !== undefined && value !== null),
    );
    const payload: StakeDbDashboardRequest = {
      ...filteredData,
      ...filteredDate,
    };
    if (!data?.stakeState) {
      delete payload.stakeState;
    } else {
      payload.stakeState = Number(data.stakeState);
    }

    const re = await readStakeDbDashboardUserList(payload);
    if (re.status !== 200) {
      return;
    }
    setList(re.data);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        overflow: 'auto',
        flex: 1,
        flexDirection: 'column',
        gap: { xs: '16px', sm: '0px' },
      }}
    >
      <Card sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Header title="Stake-DB 사용자 대시보드" />
        <Divider />
        <CardContent
          sx={{
            flex: 1,
            overflow: 'auto',
          }}
        >
          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack spacing={2}>
              <Controller
                control={control}
                name="walletAddress"
                render={({ field }) => (
                  <FormControl error={Boolean(errors.walletAddress)}>
                    <InputLabel>wallet address</InputLabel>
                    <OutlinedInput {...field} label="wallet address" type="text" />
                    {errors.walletAddress ? <FormHelperText>{errors.walletAddress.message}</FormHelperText> : null}
                  </FormControl>
                )}
              />
              <Controller
                control={control}
                name="title"
                render={({ field }) => (
                  <FormControl error={Boolean(errors.title)}>
                    <InputLabel>title</InputLabel>
                    <Select
                      labelId="title"
                      label="title"
                      {...field}
                      value={field.value || ''}
                      onChange={(e: SelectChangeEvent) => {
                        field.onChange(e.target.value);
                      }}
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {titles.map((title) => (
                        <MenuItem key={title} value={title}>
                          {title}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.title && <FormHelperText>{errors.title.message}</FormHelperText>}
                  </FormControl>
                )}
              />
              <Controller
                name="stakeState"
                control={control}
                render={({ field }) => (
                  <FormControl error={Boolean(errors.stakeState)}>
                    <InputLabel shrink htmlFor="select-multiple-native">
                      Status
                    </InputLabel>
                    <Select
                      labelId="stakeState"
                      label="stakeState"
                      {...field}
                      value={field.value || ''}
                      onChange={(e: SelectChangeEvent) => {
                        field.onChange(e.target.value);
                      }}
                    >
                      <MenuItem disabled value="">
                        <em>None</em>
                      </MenuItem>
                      {statusList.map((option) => (
                        <MenuItem key={option.label} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.stakeState && <FormHelperText>{errors.stakeState.message}</FormHelperText>}
                  </FormControl>
                )}
              />

              <Grid xs={12} sm={6}>
                <DatePickers label="시작일" setValue={setDate} poolName="startDt" defaultValue={date.startDt} />
              </Grid>
              <Grid xs={12} sm={6}>
                <DatePickers label="종료일" setValue={setDate} poolName="endDt" defaultValue={date.endDt} />
              </Grid>

              <Button type="submit" variant="contained">
                검색
              </Button>

              <Button
                type="button"
                variant="contained"
                onClick={() => {
                  reset();
                  setDate({ startDt: '', endDt: '' });
                  setList(null);
                }}
              >
                초기화
              </Button>
            </Stack>
          </form>
          <StakeList list={list?.stake} />
          <Box mb={5} />
          <UnstakeList list={list?.unstake} />
          <Box mb={5} />
          <RewardList list={list?.reward} />
          <Box mb={5} />
          <WithdrawList list={list?.withdraw} />
        </CardContent>
      </Card>
    </Box>
  );
}
